<template>
  <div class="hris-page public-vacancies-page">
    <div class="hris-page-inner">
      <div class="hris-page-header hris-page-header-row">
        <div>
          <span class="hris-page-eyebrow">Careers</span>
          <h1 class="hris-page-title">Open Positions</h1>
          <p class="hris-page-subtitle">
            Browse current openings and submit your application online.
          </p>
        </div>
        <div class="public-vacancies-actions">
          <span class="hris-badge hris-badge-accent">{{ filteredVacancies.length }} Open Roles</span>
          <q-btn unelevated class="hris-q-primary" icon="fas fa-file-signature" label="Apply Now" to="/employment-application" />
        </div>
      </div>

      <div class="hris-toolbar public-vacancies-toolbar">
        <q-input
          v-model="search"
          dense
          outlined
          color="green"
          clearable
          placeholder="Search title, department, or site"
          class="public-vacancies-search"
        >
          <template v-slot:append>
            <q-icon name="fas fa-search" />
          </template>
        </q-input>
        <div class="hris-toolbar-spacer"></div>
        <q-btn unelevated class="hris-q-outline" icon="fas fa-sync" label="Refresh" @click="loadVacancies"></q-btn>
      </div>

      <div class="hris-grid">
        <div v-for="vacancy in filteredVacancies" :key="vacancy.id" class="hris-content-card public-vacancy-card">
          <div class="hris-content-card-body">
            <div class="public-vacancy-header">
              <div>
                <div class="hris-content-card-title">{{ vacancy.title }}</div>
                <div class="hris-content-card-meta">
                  {{ vacancy.department || 'General' }} &bull; {{ vacancy.site || 'Any site' }}
                </div>
              </div>
              <span class="hris-badge" :class="vacancy.status === 'published' ? 'hris-badge-success' : 'hris-badge-neutral'">
                {{ normalizeLabel(vacancy.status) }}
              </span>
            </div>

            <div class="public-vacancy-meta q-mt-md">
              <span><q-icon name="fas fa-briefcase" class="q-mr-xs"></q-icon>{{ vacancy.employment_type }}</span>
              <span><q-icon name="fas fa-users" class="q-mr-xs"></q-icon>{{ vacancy.headcount }} opening(s)</span>
              <span v-if="vacancy.salary_min || vacancy.salary_max">
                <q-icon name="fas fa-peso-sign" class="q-mr-xs"></q-icon>
                {{ formatSalaryRange(vacancy.salary_min, vacancy.salary_max) }}
              </span>
            </div>

            <div class="public-vacancy-block q-mt-md">
              <div class="hris-content-card-meta q-mb-xs">Job Description</div>
              <p class="hris-content-card-text">{{ vacancy.job_description || 'No description provided.' }}</p>
            </div>

            <div class="public-vacancy-block">
              <div class="hris-content-card-meta q-mb-xs">Requirements</div>
              <p class="hris-content-card-text">{{ vacancy.requirements || 'No requirements listed.' }}</p>
            </div>
          </div>

          <div class="hris-content-card-footer public-vacancy-footer">
            <div class="hris-content-card-meta">
              Opened {{ formatDate(vacancy.open_date) }}
            </div>
            <q-btn
              unelevated
              class="hris-q-primary"
              icon="fas fa-arrow-right"
              label="Apply"
              :to="{ path: '/employment-application', query: { vacancy_id: vacancy.id } }"
            ></q-btn>
          </div>
        </div>
      </div>

      <div v-if="!loading && filteredVacancies.length === 0" class="hris-empty public-vacancies-empty q-mt-lg">
        No open vacancies match your search.
      </div>

      <div v-if="loading" class="hris-loading public-vacancies-loading q-mt-lg">
        <q-spinner color="green" size="2em"></q-spinner>
        <div class="q-mt-sm">Loading vacancies...</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PublicVacancies',
  data() {
    return {
      loading: false,
      search: '',
      vacancies: [],
      apiUrl: process.env.VUE_APP_API_URL
    };
  },
  computed: {
    filteredVacancies() {
      const search = String(this.search || '').trim().toLowerCase();
      if (!search) {
        return this.vacancies;
      }

      return this.vacancies.filter(vacancy => {
        const haystack = [
          vacancy.title,
          vacancy.department,
          vacancy.site,
          vacancy.employment_type,
          vacancy.job_description,
          vacancy.requirements
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return haystack.includes(search);
      });
    }
  },
  mounted() {
    this.loadVacancies();
  },
  methods: {
    loadVacancies() {
      this.loading = true;
      axios
        .get(`${this.apiUrl}/recruitments/vacancies/public`)
        .then(res => {
          this.vacancies = res.data.data || [];
        })
        .catch(() => {
          this.vacancies = [];
          this.$q.notify({
            type: 'negative',
            message: 'Unable to load vacancies.'
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    formatDate(value) {
      if (!value) return 'N/A';
      return String(value).slice(0, 10);
    },
    formatSalaryRange(min, max) {
      const hasMin = min !== null && min !== undefined && min !== '';
      const hasMax = max !== null && max !== undefined && max !== '';
      if (!hasMin && !hasMax) return 'Negotiable';
      const formattedMin = hasMin ? Number(min).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
      const formattedMax = hasMax ? Number(max).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
      if (hasMin && hasMax) return `${formattedMin} - ${formattedMax}`;
      return hasMin ? `From ${formattedMin}` : `Up to ${formattedMax}`;
    },
    normalizeLabel(value) {
      return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
  }
};
</script>

<style scoped>
.public-vacancies-actions,
.public-vacancies-toolbar,
.public-vacancy-header,
.public-vacancy-footer {
  display: flex;
  align-items: center;
  gap: 12px;
}

.public-vacancies-toolbar {
  margin-bottom: 20px;
}

.public-vacancies-search {
  min-width: 320px;
  max-width: 520px;
  flex: 1 1 320px;
}

.public-vacancy-card {
  height: 100%;
}

.public-vacancy-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  font-size: 0.82rem;
  color: var(--hris-text-muted);
}

.public-vacancy-block {
  display: grid;
  gap: 6px;
}

.public-vacancies-empty,
.public-vacancies-loading {
  min-height: 180px;
}

@media (max-width: 600px) {
  .public-vacancies-actions,
  .public-vacancies-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .public-vacancies-search {
    min-width: 0;
    max-width: none;
  }
}
</style>
